import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface SearchableSelectItem {
  id: string | number;
  name: string;
}

// A searchable dropdown for long option lists (Pathao's zone/area lists run
// to 100+ entries) — a plain <select> for that many items renders as a huge,
// unstyleable native popup (especially bad in Firefox) with no way to type
// to filter. This renders its own panel instead, so it looks consistent
// across browsers and lets staff just type a few letters.
export function SearchableSelect({
  items,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
}: {
  items: SearchableSelectItem[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = items.find((i) => String(i.id) === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.name.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const toggle = () => {
    if (disabled || loading) return;
    setQuery('');
    setOpen((o) => !o);
  };

  const pick = (id: string | number) => {
    onChange(String(id));
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        disabled={disabled || loading}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] text-left bg-surface-muted border border-border text-ink transition-colors duration-150 hover:border-primary-200 focus:outline-none focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
      >
        <span className={`truncate ${selected ? 'text-ink' : 'text-ink-disabled'}`}>
          {loading ? 'Loading…' : selected ? selected.name : placeholder}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 w-full rounded-xl border border-border-subtle bg-white shadow-modal overflow-hidden">
          <div className="relative border-b border-border-subtle">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full pl-8 pr-3 py-2 text-[13px] outline-none bg-transparent text-ink"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2.5 text-[12.5px] text-ink-faint">No matches</p>
            ) : (
              filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => pick(item.id)}
                  className={`w-full text-left px-3 py-2 text-[13px] cursor-pointer hover:bg-surface-muted ${
                    String(item.id) === value ? 'bg-primary-50 text-primary-800 font-medium' : 'text-ink'
                  }`}
                >
                  {item.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
